import {
    memo,
    useEffect,
    useState,
    type ChangeEvent,
    type ReactNode
} from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import SearchIcon from '@mui/icons-material/Search';
import {
    DataGridPremium,
    type GridColDef,
    type GridRenderCellParams,
    type GridValueGetterParams
} from '@mui/x-data-grid-premium';
import type { Merchant } from 'features/merchants/types';
import Iconify from 'lib/iconify';
import NoDataOverlay from 'ui/molecules/TableGrids/Table/NoDataOverlay';
import {
    KYC_SERVICE_TYPE_LABELS,
    SERVICE_COLUMN_TYPES,
    formatServiceState,
    formatStatus
} from './formatters';
import IncludedMethodsCell from './IncludedMethodsCell';
import useMerchantKycSummaries from './useMerchantKycSummaries';
import type { MerchantKycSummary } from './types';

const STATUS_OPTIONS = ['all', 'active', 'inactive'] as const;
type StatusFilter = typeof STATUS_OPTIONS[number];

type Props = {
    readonly action?: ReactNode;
    readonly onEdit: (merchant: Merchant) => void;
    readonly reloadToken?: number;
};

const gridSx = {
    '& .MuiDataGrid-columnHeaderTitle': {
        color: '#212B36',
        fontWeight: 600
    }
};

const MerchantKycSettingsTable = ({ action, onEdit, reloadToken }: Props) => {
    const { summaries, isLoading, reload } = useMerchantKycSummaries();

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

    useEffect(() => {
        if (reloadToken !== undefined) {
            reload();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadToken]);

    const onSearchChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
        setSearch(target.value);
    };

    const onStatusFilterChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
        setStatusFilter(target.value as StatusFilter);
    };

    const normalizedSearch = search.trim().toLowerCase();

    const matchesSearch = (summary: MerchantKycSummary) => {
        if (!normalizedSearch) return true;
        const haystack: Array<string> = [
            summary.merchant.fullName ?? '',
            formatStatus(summary.merchant.isActive),
            ...SERVICE_COLUMN_TYPES.map(
                t => formatServiceState(summary.serviceStates[t])
            ),
            ...summary.includedMethods.map(m => m.name)
        ];
        return haystack.some(value => value?.toLowerCase().includes(normalizedSearch));
    };

    const matchesStatus = (summary: MerchantKycSummary) => {
        if (statusFilter === 'all') return true;
        const active = Boolean(summary.merchant.isActive);
        return statusFilter === 'active' ? active : !active;
    };

    const filtered = summaries.filter(s => matchesSearch(s) && matchesStatus(s));

    const columns: GridColDef[] = [
        {
            field: 'merchant',
            headerName: 'Merchant',
            flex: 1,
            minWidth: 180,
            valueGetter: ({ row }: GridValueGetterParams<unknown, MerchantKycSummary>) =>
                row.merchant.fullName ?? ''
        },
        {
            field: 'status',
            headerName: 'Merchant Status',
            minWidth: 150,
            valueGetter: ({ row }: GridValueGetterParams<unknown, MerchantKycSummary>) =>
                formatStatus(row.merchant.isActive),
            renderCell: ({ value }: GridRenderCellParams<string>) => (
                <Chip
                    label={value}
                    size='small'
                    color={value === 'Active' ? 'success' : 'default'}
                    sx={{ fontWeight: 600 }}
                />
            )
        },
        ...SERVICE_COLUMN_TYPES.map<GridColDef>(type => ({
            field: `service_${type}`,
            headerName: KYC_SERVICE_TYPE_LABELS[type],
            minWidth: 130,
            sortable: false,
            valueGetter: ({ row }: GridValueGetterParams<unknown, MerchantKycSummary>) =>
                formatServiceState(row.serviceStates[type])
        })),
        {
            field: 'includedMethods',
            headerName: 'Include Methods',
            flex: 1,
            minWidth: 220,
            sortable: false,
            valueGetter: ({ row }: GridValueGetterParams<unknown, MerchantKycSummary>) =>
                row.includedMethods.map(m => m.name).join(', '),
            renderCell: ({ row }: GridRenderCellParams<unknown, MerchantKycSummary>) => (
                <IncludedMethodsCell methods={row.includedMethods} />
            )
        },
        {
            field: 'actions',
            headerName: '',
            width: 70,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'right',
            headerAlign: 'right',
            renderCell: ({ row }: GridRenderCellParams<unknown, MerchantKycSummary>) => (
                <Tooltip title='Edit KYC settings'>
                    <IconButton
                        size='small'
                        onClick={() => onEdit(row.merchant)}
                        data-test-id={`merchantKycSettings.edit.${row.merchant.coreId}`}
                    >
                        <Iconify icon='solar:pen-bold' />
                    </IconButton>
                </Tooltip>
            )
        }
    ];

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 2,
                    flexWrap: 'wrap'
                }}
            >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                        type='search'
                        size='small'
                        placeholder='Search merchants'
                        value={search}
                        onChange={onSearchChange}
                        disabled={isLoading}
                        sx={{ width: 320 }}
                        inputProps={{ 'data-test-id': 'merchantKycSettings.search' }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon fontSize='small' />
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        select
                        size='small'
                        label='Status'
                        value={statusFilter}
                        onChange={onStatusFilterChange}
                        disabled={isLoading}
                        sx={{ width: 160 }}
                        inputProps={{ 'data-test-id': 'merchantKycSettings.statusFilter' }}
                    >
                        <MenuItem value='all'>All</MenuItem>
                        <MenuItem value='active'>Active</MenuItem>
                        <MenuItem value='inactive'>Inactive</MenuItem>
                    </TextField>
                </Box>
                {action}
            </Box>

            <Box sx={{ width: '100%' }}>
                <DataGridPremium
                    autoHeight
                    rows={filtered}
                    columns={columns}
                    getRowId={(row) =>
                        String((row as MerchantKycSummary).merchant.coreId)}
                    loading={isLoading}
                    disableSelectionOnClick
                    density='compact'
                    pagination
                    rowsPerPageOptions={[10, 25, 50]}
                    initialState={{
                        pagination: {
                            pageSize: 10
                        }
                    }}
                    pinnedColumns={{ right: ['actions'] }}
                    components={{ NoRowsOverlay: NoDataOverlay }}
                    sx={gridSx}
                />
            </Box>
        </>
    );
};

export default memo(MerchantKycSettingsTable);
